import React, { useEffect } from 'react';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { useShallow } from 'zustand/react/shallow';
import { useWalletStore } from './stores/walletStore';
import { useAirdropStore } from './stores/airdropStore';
import { useTokenStore } from './stores/tokenStore';
import Background from './components/Background';
import Header from './components/Header';
import AirdropCard from './components/AirdropCard';
import Toast from './components/Toast';
import { useUserCheck } from './hooks/useUserCheck';

/**
 * App - Main application component
 * 
 * Responsibilities:
 * - Web3 client management
 * - Wallet connection state synchronization
 * - Component composition and layout
 */
const App: React.FC = () => {
  // Wagmi hooks for wallet connection
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient, isLoading: isWalletClientLoading } = useWalletClient();

  // Wallet store
  const setClients = useWalletStore((state) => state.setClients);
  const connectWallet = useWalletStore((state) => state.connect);
  const disconnectWallet = useWalletStore((state) => state.disconnect);

  // Airdrop store - use useShallow to optimize state subscription
  const {
    status,
    verificationStatus,
    isClaimed,
    allocation,
    error,
    humanStatusChecked,
    palmVerified,
    palmStatusLoading,
    loadingState,
    showVerificationModal,
    verificationLoading,
    verificationError,
    verificationSuccess,
    userExists,
  } = useAirdropStore(
    useShallow((state) => ({
      status: state.status,
      verificationStatus: state.verificationStatus,
      isClaimed: state.isClaimed,
      allocation: state.allocation,
      error: state.error,
      humanStatusChecked: state.humanStatusChecked,
      palmVerified: state.palmVerified,
      palmStatusLoading: state.palmStatusLoading,
      loadingState: state.loadingState,
      showVerificationModal: state.showVerificationModal,
      verificationLoading: state.verificationLoading,
      verificationError: state.verificationError,
      verificationSuccess: state.verificationSuccess,
      userExists: state.userExists,
    }))
  );

  // Airdrop actions
  const checkClaimStatus = useAirdropStore((state) => state.checkClaimStatus);
  const checkHumanStatus = useAirdropStore((state) => state.checkHumanStatus);
  const claimAirdrop = useAirdropStore((state) => state.claimAirdrop);
  const resetAirdrop = useAirdropStore((state) => state.resetAll);
  const openVerificationModal = useAirdropStore((state) => state.openVerificationModal);
  const closeVerificationModal = useAirdropStore((state) => state.closeVerificationModal);
  const performVerification = useAirdropStore((state) => state.performVerification);

  // Token store
  const tokenBalance = useTokenStore((state) => state.balance);
  const tokenSymbol = useTokenStore((state) => state.symbol);
  const fetchTokenBalance = useTokenStore((state) => state.fetchBalance);
  const fetchTokenInfo = useTokenStore((state) => state.fetchTokenInfo);
  const resetToken = useTokenStore((state) => state.reset);

  // User registration check with automatic polling
  useUserCheck(isConnected ? address : undefined);

  // Set blockchain clients
  useEffect(() => {
    setClients(publicClient ?? null, walletClient ?? null);
  }, [publicClient, walletClient, setClients]);

  // Fetch token info on mount
  useEffect(() => {
    void fetchTokenInfo();
  }, [fetchTokenInfo]);

  // Track previous address to detect wallet switches
  const prevAddressRef = React.useRef<string | undefined>();

  // Handle wallet connection and address changes
  useEffect(() => {
    const currentAddress = address;
    const prevAddress = prevAddressRef.current;

    // Detect wallet switch: address changed while still connected
    const isWalletSwitch = isConnected && currentAddress && prevAddress && currentAddress !== prevAddress;

    if (isWalletSwitch) {
      // Reset state when switching wallets
      console.log('[App] Wallet switched from', prevAddress, 'to', currentAddress);
      resetAirdrop();
      resetToken();
    }

    if (isConnected && address) {
      // Connect wallet in wallet store
      connectWallet(address);

      console.log('[App] Fetching data for address:', address);

      // Fetch token balance for new address
      void fetchTokenBalance(address);

      // Automatically check claim status and human status
      // User registration check is handled by useUserCheck hook
      void checkClaimStatus(false);
      void checkHumanStatus(false);
    } else if (!isConnected) {
      // Disconnect wallet and reset airdrop state
      console.log('[App] Wallet disconnected, resetting state');
      disconnectWallet();
      resetAirdrop();
      resetToken();
    }

    // Update previous address ref
    prevAddressRef.current = currentAddress;
  }, [isConnected, address, connectWallet, disconnectWallet, checkClaimStatus, checkHumanStatus, resetAirdrop, fetchTokenBalance, resetToken]);

  return (
    <div className="h-screen w-full flex flex-col items-center justify-center p-2 md:p-4 relative overflow-hidden">
      {/* Toast Notifications */}
      <Toast />

      {/* Background */}
      <Background />

      {/* Header */}
      <Header />

      {/* Main Content */}
      <AirdropCard
        address={address}
        isConnected={isConnected}
        status={status}
        verificationStatus={verificationStatus}
        isClaimed={isClaimed}
        allocation={allocation}
        tokenBalance={tokenBalance}
        tokenSymbol={tokenSymbol}
        humanStatusChecked={humanStatusChecked}
        palmVerified={palmVerified}
        palmStatusLoading={palmStatusLoading}
        loadingState={loadingState}
        error={error}
        showVerificationModal={showVerificationModal}
        verificationLoading={verificationLoading}
        verificationError={verificationError}
        verificationSuccess={verificationSuccess}
        userExists={userExists}
        isWalletClientLoading={isWalletClientLoading}
        onClaimAirdrop={() => void claimAirdrop()}
        onRetry={async () => {
          await claimAirdrop()
          checkHumanStatus()
        }}
        onOpenVerificationModal={openVerificationModal}
        onCloseVerificationModal={closeVerificationModal}
        onPerformVerification={() => void performVerification()}
      /></div>
  );
};

export default App;
